import { useState } from "react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { UploadSimple, X, CheckCircle } from "@phosphor-icons/react";

export default function Uploader({ purpose = "general", value, onUploaded, label = "Upload", accept = "image/*", testid = "uploader" }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File terlalu besar (maks 8MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", purpose);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.path);
      toast.success("Berhasil diunggah");
    } catch (e) {
      toast.error("Gagal mengunggah file");
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const clear = () => {
    setPreview(null);
    onUploaded(null);
  };

  return (
    <div className="w-full" data-testid={testid}>
      <label className="block text-xs tracking-widest text-white/60 uppercase mb-2 font-display">{label}</label>
      {!value ? (
        <label className="flex flex-col items-center justify-center border border-dashed border-[#27272A] hover:border-[#FF3B30] transition-colors p-6 cursor-pointer bg-[#0A0A0A]">
          <UploadSimple size={32} color="#FF3B30" weight="regular" />
          <span className="mt-2 text-sm text-white/70">{uploading ? "Mengunggah..." : "Klik atau seret file ke sini"}</span>
          <span className="text-xs text-white/40 mt-1">JPG / PNG / PDF · Maks 8MB</span>
          <input
            type="file"
            accept={accept}
            className="hidden"
            data-testid={`${testid}-input`}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </label>
      ) : (
        <div className="border border-[#27272A] p-3 bg-[#0A0A0A] flex items-center gap-3">
          {preview ? (
            <img src={preview} alt="" className="w-16 h-16 object-cover border border-[#27272A]" />
          ) : (
            <div className="w-16 h-16 border border-[#27272A] flex items-center justify-center">
              <CheckCircle size={24} color="#34D399" weight="fill" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs text-emerald-400 font-display tracking-widest">UPLOADED</div>
            <div className="text-xs text-white/60 truncate font-mono-num">{value}</div>
          </div>
          <button onClick={clear} className="text-white/60 hover:text-[#FF3B30]" data-testid={`${testid}-clear`}>
            <X size={20} />
          </button>
        </div>
      )}
    </div>
  );
}

export function MultiUploader({ purpose = "general", values = [], onChange, label = "Lampiran", testid = "multi-upload", max = 5 }) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (values.length >= max) {
      toast.error(`Maksimal ${max} lampiran`);
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File terlalu besar (maks 8MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", purpose);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange([...values, data.path]);
      toast.success("Berhasil diunggah");
    } catch (e) {
      toast.error("Gagal mengunggah file");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="w-full" data-testid={testid}>
      <label className="block text-xs tracking-widest text-white/60 uppercase mb-2 font-display">{label} (maks {max})</label>
      <div className="flex flex-wrap gap-3">
        {values.map((p, i) => (
          <div key={p} className="relative border border-[#27272A] bg-[#0A0A0A]" data-testid={`${testid}-item-${i}`}>
            <img src={fileUrl(p)} alt={`lampiran ${i + 1}`} className="w-20 h-20 object-cover" />
            <button type="button" onClick={() => onChange(values.filter((x) => x !== p))} className="absolute -top-2 -right-2 w-6 h-6 bg-[#FF3B30] text-white flex items-center justify-center" data-testid={`${testid}-del-${i}`}>
              <X size={14} />
            </button>
          </div>
        ))}
        {values.length < max && (
          <label className="w-20 h-20 border border-dashed border-[#27272A] hover:border-[#FF3B30] transition-colors cursor-pointer flex flex-col items-center justify-center bg-[#0A0A0A]">
            <UploadSimple size={20} color="#FF3B30" />
            <span className="text-[9px] text-white/50 mt-1 tracking-widest">{uploading ? "UPLOAD..." : "TAMBAH"}</span>
            <input type="file" accept="image/*" className="hidden" data-testid={`${testid}-input`} onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
        )}
      </div>
    </div>
  );
}
